// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * TotalsRow component for displaying aggregated totals across all products and locations.
 */
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { AssetCell } from '@/inventory/components/overview/components/asset-cell';
import { INVENTORY_OVERVIEW_COLORS } from '@/inventory/components/overview/constants';
import {
  cellStyles,
  gridRowStyles,
  totalCellStyles,
} from '@/inventory/components/overview/styles';
import {
  OverviewLocation,
  OverviewTotals,
} from '@/inventory/components/overview/types';
import { navigateToAssets } from '@/inventory/components/overview/utils/asset-navigation';
import { Box, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';

interface TotalsRowProps {
  totals: OverviewTotals;
  inventoryLocations: OverviewLocation[];
  nonInventoryLocations: OverviewLocation[];
  isInventoryExpanded: boolean;
  isNonInventoryExpanded: boolean;
  isInTransitExpanded: boolean;
  gridTemplate: string;
  showScrappedAssets?: boolean;
  hideZeroes?: boolean;
}

/**
 * TotalsRow component
 */
export function TotalRows(props: TotalsRowProps) {
  const {
    totals,
    inventoryLocations,
    nonInventoryLocations,
    isInventoryExpanded,
    isNonInventoryExpanded,
    isInTransitExpanded,
    gridTemplate,
    showScrappedAssets = false,
    hideZeroes = false,
  } = props;

  const navigate = useNavigate();

  const handleLocationTotalClick = (
    locationId: string,
    status: AssetFunctionalStatus
  ) => {
    navigateToAssets({
      locationIds: [locationId],
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleLocationTotalNumberClick = (locationId: string) => {
    navigateToAssets({
      locationIds: [locationId],
      showScrappedAssets,
      navigate,
    });
  };

  const handleInventoryTotalClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      inventoryStatus: ['in_inventory'],
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleInventoryTotalNumberClick = () => {
    navigateToAssets({
      inventoryStatus: ['in_inventory'],
      showScrappedAssets,
      navigate,
    });
  };

  const handleNonInventoryTotalClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      inventoryStatus: ['not_in_inventory'],
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleNonInventoryTotalNumberClick = () => {
    navigateToAssets({
      inventoryStatus: ['not_in_inventory'],
      showScrappedAssets,
      navigate,
    });
  };

  const handleInTransitClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      transitStatus: ['in_transit_to_inventory', 'in_transit_to_field'],
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleInTransitTotalNumberClick = () => {
    navigateToAssets({
      transitStatus: ['in_transit_to_inventory', 'in_transit_to_field'],
      showScrappedAssets,
      navigate,
    });
  };

  const handleInTransitToInventoryClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      transitStatus: ['in_transit_to_inventory'],
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleInTransitToInventoryTotalClick = () => {
    navigateToAssets({
      transitStatus: ['in_transit_to_inventory'],
      showScrappedAssets,
      navigate,
    });
  };

  const handleInTransitToNonInventoryClick = (
    status: AssetFunctionalStatus
  ) => {
    navigateToAssets({
      transitStatus: ['in_transit_to_field'],
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleInTransitToNonInventoryTotalClick = () => {
    navigateToAssets({
      transitStatus: ['in_transit_to_field'],
      showScrappedAssets,
      navigate,
    });
  };

  const handleGrandTotalClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      functionalStatus: status,
      showScrappedAssets,
      navigate,
    });
  };

  const handleGrandTotalNumberClick = () => {
    navigateToAssets({
      showScrappedAssets,
      navigate,
    });
  };

  return (
    <Box
      sx={{
        ...gridRowStyles.totalRow,
        gridTemplateColumns: gridTemplate,
      }}
    >
      {/* Total label */}
      <Box sx={cellStyles.totalLabel}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Total
        </Typography>
      </Box>

      {/* Inventory location totals */}
      {isInventoryExpanded &&
        inventoryLocations.map((location) => (
          <Box
            key={location.location.key}
            sx={{
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:
                INVENTORY_OVERVIEW_COLORS.INVENTORY.LOCATION_BACKGROUND,
            }}
          >
            <AssetCell
              counts={location.totalCounts}
              onStatusClick={(status) =>
                handleLocationTotalClick(location.location.id, status)
              }
              onTotalClick={() =>
                handleLocationTotalNumberClick(location.location.id)
              }
              isTotal
              showScrappedAssets={showScrappedAssets}
              hideZeroes={hideZeroes}
            />
          </Box>
        ))}

      {/* Inventory total - always show */}
      <Box
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: INVENTORY_OVERVIEW_COLORS.INVENTORY.TOTAL_BACKGROUND,
        }}
      >
        <AssetCell
          counts={totals.inventoryTotal}
          onStatusClick={handleInventoryTotalClick}
          onTotalClick={handleInventoryTotalNumberClick}
          isTotal
          showScrappedAssets={showScrappedAssets}
          hideZeroes={hideZeroes}
        />
      </Box>

      {/* Non-inventory location totals */}
      {isNonInventoryExpanded &&
        nonInventoryLocations.map((location) => (
          <Box
            key={location.location.key}
            sx={{
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:
                INVENTORY_OVERVIEW_COLORS.NON_INVENTORY.LOCATION_BACKGROUND,
            }}
          >
            <AssetCell
              counts={location.totalCounts}
              onStatusClick={(status) =>
                handleLocationTotalClick(location.location.id, status)
              }
              onTotalClick={() =>
                handleLocationTotalNumberClick(location.location.id)
              }
              isTotal
              showScrappedAssets={showScrappedAssets}
              hideZeroes={hideZeroes}
            />
          </Box>
        ))}

      {/* Non-inventory total - always show */}
      <Box
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor:
            INVENTORY_OVERVIEW_COLORS.NON_INVENTORY.TOTAL_BACKGROUND,
        }}
      >
        <AssetCell
          counts={totals.nonInventoryTotal}
          onStatusClick={handleNonInventoryTotalClick}
          onTotalClick={handleNonInventoryTotalNumberClick}
          isTotal
          showScrappedAssets={showScrappedAssets}
          hideZeroes={hideZeroes}
        />
      </Box>

      {/* Individual in-transit columns */}
      {isInTransitExpanded && (
        <>
          {/* In transit to inventory */}
          <Box
            sx={{
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:
                INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.LOCATION_BACKGROUND,
            }}
          >
            <AssetCell
              counts={totals.inTransitToInventoryTotal}
              onStatusClick={handleInTransitToInventoryClick}
              onTotalClick={handleInTransitToInventoryTotalClick}
              isTotal
              showScrappedAssets={showScrappedAssets}
              hideZeroes={hideZeroes}
            />
          </Box>

          {/* In transit to the field */}
          <Box
            sx={{
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:
                INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.LOCATION_BACKGROUND,
            }}
          >
            <AssetCell
              counts={totals.inTransitToNonInventoryTotal}
              onStatusClick={handleInTransitToNonInventoryClick}
              onTotalClick={handleInTransitToNonInventoryTotalClick}
              isTotal
              showScrappedAssets={showScrappedAssets}
              hideZeroes={hideZeroes}
            />
          </Box>
        </>
      )}

      {/* In transit total */}
      <Box
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor:
            INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TOTAL_BACKGROUND,
        }}
      >
        <AssetCell
          counts={totals.inTransitTotal}
          onStatusClick={handleInTransitClick}
          onTotalClick={handleInTransitTotalNumberClick}
          isTotal
          showScrappedAssets={showScrappedAssets}
          hideZeroes={hideZeroes}
        />
      </Box>

      {/* Empty flexible space */}
      <Box sx={{ backgroundColor: 'grey.50' }} />

      {/* Grand total */}
      <Box sx={totalCellStyles.grand}>
        <AssetCell
          counts={totals.grandTotal}
          onStatusClick={handleGrandTotalClick}
          onTotalClick={handleGrandTotalNumberClick}
          isTotal
          showScrappedAssets={showScrappedAssets}
          hideZeroes={hideZeroes}
        />
      </Box>
    </Box>
  );
}
